/**
 * Step 4 of 4 - Duration confirmation and create.
 *
 * Validates duration >= planned topics, then:
 *   1) trainer availability
 *   2) POST create-sessionsep with session_provider=livekit
 *   3) optional → add-students prompt; workshop → add-batches → Assign Sessions;
 *      personal → add-students; else View Sessions
 */

import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { AppLayout, PageMain } from '../components/WizardShell';
import { ArrowLeft } from 'lucide-react';
import { ROUTES } from '../utils/createRoutes';
import {
  buildDurationOptions,
  buildQaDurationOptions,
  defaultSessionForm,
  formatDurationLabel,
} from '../utils/scheduleFormUtils';
import { questionBankOptions } from '../data/topicPlannerConfig';
import { calcSessionDuration, formatDurationMinutes } from '../types/topicQuiz';
import { loadPlannerTopics } from '../utils/topicPlannerStorage';
import { formatSessionDateTime, type SessionCategory } from '../utils/scheduledSessionStorage';
import { parseDurationInput } from '../utils/parseDurationInput';
import { resolveLiveSessionName, LIVE_SESSION_NAMING_CONVENTION } from '../utils/liveSessionNaming';
import { isWorkshopCategory } from '../utils/sessionCategoryUtils';
import {
  clearAdminCreateSessionFlow,
  isAdminCreateSessionFlow,
  loadCreateSessionDraft,
  markCreateFlowExit,
  patchCreateSessionDraft,
  peekCreateFlowExit,
  saveCreateSessionDraft,
} from '../utils/createSessionFlowStorage';
import {
  computeWorkshopSchedule,
  mergeDayTimings,
  type WorkshopDayTiming,
} from '../utils/workshopSessionUtils';
import { createLiveKitSessionFromDraft } from '../utils/createLiveKitSession';
import { authService } from '../../../utils/authService';
import {
  conflictSessionId,
  findSelfTrainerConflict,
  otherTrainerConflicts,
  shouldCheckTrainerAvailability,
} from '../utils/trainerAvailability';
import {
  clampQaDurationMinutes,
  MAX_QA_DURATION_MINUTES,
  MIN_QA_DURATION_MINUTES,
  setQaDurationForSession,
} from '../utils/sessionQaStorage';
import ManageSessionStudentsModal from '../../../LMS/TR/files/sessionUtils/ManageSessionStudentsModal';
import type { Session } from '../../../LMS/TR/files/sessionUtils/sessionTypes';

const labelClass = 'form-label';

function toDateString(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function toTimeString(date: Date): string {
  return `${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`;
}

function DateTimeField({
  label,
  date,
  time,
  onDateChange,
  onTimeChange,
}: {
  label: string;
  date: string;
  time: string;
  onDateChange: (value: string) => void;
  onTimeChange: (value: string) => void;
}) {
  return (
    <div>
      <p className={labelClass}>{label}</p>
      <div className="row g-2">
        <div className="col-6">
          <label className="form-label small text-muted mb-1">Date</label>
          <input
            type="date"
            value={date}
            onChange={(e) => onDateChange(e.target.value)}
            className="form-control"
          />
        </div>
        <div className="col-6">
          <label className="form-label small text-muted mb-1">Time</label>
          <input
            type="time"
            value={time}
            onChange={(e) => onTimeChange(e.target.value)}
            className="form-control"
          />
        </div>
      </div>
    </div>
  );
}

function addMinutesToStart(startDate: string, startTime: string, minutes: number) {
  const start = new Date(`${startDate}T${startTime}:00`);
  if (Number.isNaN(start.getTime())) return { endDate: startDate, endTime: startTime };
  const end = new Date(start.getTime() + minutes * 60_000);
  return { endDate: toDateString(end), endTime: toTimeString(end) };
}

/** Total calendar span = content duration + Q&A window. */
function totalSessionSpanMinutes(contentMinutes: number, qaMinutes: number): number {
  const content = Math.max(0, contentMinutes);
  const qa = Number.isFinite(qaMinutes) && qaMinutes > 0 ? qaMinutes : 0;
  return content + qa;
}

function minutesBetweenStartAndEnd(
  startDate: string,
  startTime: string,
  endDate: string,
  endTime: string,
): number | null {
  const start = new Date(`${startDate}T${startTime}:00`);
  const end = new Date(`${endDate}T${endTime}:00`);
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return null;
  return Math.max(0, Math.round((end.getTime() - start.getTime()) / 60_000));
}

export function ScheduleSession() {
  const navigate = useNavigate();
  const adminCreateFlow = isAdminCreateSessionFlow();
  const createDraft = adminCreateFlow ? loadCreateSessionDraft() : null;
  const [showOptionalAssignPrompt, setShowOptionalAssignPrompt] = useState(false);
  const [showWorkshopBatchPrompt, setShowWorkshopBatchPrompt] = useState(false);
  const [createdOptionalSession, setCreatedOptionalSession] = useState<{
    sessionId: number;
    sessionName: string;
    rolePrefix: '/admin' | '/trainer';
    trackId: string;
    courseId: string;
    batchId: string;
    batchIds: string[];
    subjects: string[];
  } | null>(null);
  const [createdWorkshopSession, setCreatedWorkshopSession] = useState<{
    sessionId: number;
    sessionName: string;
    rolePrefix: '/admin' | '/trainer';
    trackId: string;
    courseId: string;
    sessionCategory: string;
    startDate: string;
    startTime: string;
  } | null>(null);
  const [manageStudentsSession, setManageStudentsSession] = useState<Session | null>(null);

  useEffect(() => {
    // Keep schedule page mounted while post-create assign prompts are open.
    if (
      showOptionalAssignPrompt ||
      showWorkshopBatchPrompt ||
      manageStudentsSession
    ) {
      return;
    }
    if (!adminCreateFlow || !createDraft) {
      // Success path clears draft on purpose — don't bounce to Step 1.
      const exit = peekCreateFlowExit();
      if (exit === 'listing') {
        navigate(ROUTES.viewSessions, { replace: true });
        return;
      }
      if (exit === 'suppress') return;
      navigate(ROUTES.createSession, { replace: true });
    }
  }, [
    adminCreateFlow,
    createDraft,
    navigate,
    showOptionalAssignPrompt,
    showWorkshopBatchPrompt,
    manageStudentsSession,
  ]);

  const topics = loadPlannerTopics();
  const isWorkshop = isWorkshopCategory(createDraft?.sessionCategory);
  const initialStartDate = createDraft?.startDate ?? '';
  const initialStartTime = createDraft?.startTime ?? '';
  const [startDate, setStartDate] = useState(initialStartDate);
  const [startTime, setStartTime] = useState(initialStartTime);
  const workshopDayCount = Math.max(
    1,
    createDraft?.workshopDays || createDraft?.dayTopics?.length || 1,
  );
  const [dayTimings, setDayTimings] = useState<WorkshopDayTiming[]>(() =>
    mergeDayTimings(
      workshopDayCount,
      initialStartDate,
      initialStartTime,
      createDraft?.daySchedules,
    ),
  );

  const workshopSchedule = useMemo(() => {
    if (!isWorkshop || !createDraft) return null;
    const durationByTopic = Object.fromEntries(
      topics
        .filter((t) => t.durationMinutes > 0)
        .map((t) => [t.title, t.durationMinutes]),
    );
    return computeWorkshopSchedule(startDate, startTime, createDraft.dayTopics, {
      dayTimings: mergeDayTimings(
        workshopDayCount,
        startDate,
        startTime,
        dayTimings,
      ),
      durationByTopic,
    });
  }, [
    isWorkshop,
    createDraft,
    startDate,
    startTime,
    topics,
    dayTimings,
    workshopDayCount,
  ]);

  const plannerDuration = calcSessionDuration(topics, questionBankOptions);
  const workshopPlannedMinutes = workshopSchedule?.totalDurationMinutes ?? 0;
  const plannedMinutes = isWorkshop ? workshopPlannedMinutes : plannerDuration.totalMinutes;
  const durationOptions = useMemo(
    () => buildDurationOptions(plannerDuration.totalMinutes),
    [plannerDuration.totalMinutes],
  );
  const qaDurationOptions = useMemo(() => buildQaDurationOptions(), []);

  const initialDuration =
    createDraft?.durationMinutes ??
    (isWorkshop ? workshopPlannedMinutes : plannerDuration.totalMinutes);

  const workshopInitialEnd = workshopSchedule
    ? { endDate: workshopSchedule.endDate, endTime: workshopSchedule.endTime }
    : null;
  const initialQaMinutes =
    typeof createDraft?.qaDurationMinutes === 'number' &&
    Number.isFinite(createDraft.qaDurationMinutes)
      ? createDraft.qaDurationMinutes
      : defaultSessionForm.qaDurationMinutes;
  const initialEnd =
    workshopInitialEnd ??
    (initialStartDate && initialStartTime
      ? addMinutesToStart(
          initialStartDate,
          initialStartTime,
          totalSessionSpanMinutes(initialDuration, initialQaMinutes),
        )
      : { endDate: '', endTime: '' });

  // If workshop schedule exists, still extend final end by Q&A window.
  const workshopEndWithQa = workshopInitialEnd
    ? addMinutesToStart(
        workshopInitialEnd.endDate,
        workshopInitialEnd.endTime,
        initialQaMinutes,
      )
    : null;

  const [endDate, setEndDate] = useState(
    () =>
      createDraft?.endDate ||
      workshopEndWithQa?.endDate ||
      initialEnd.endDate,
  );
  const [endTime, setEndTime] = useState(
    () =>
      createDraft?.endTime ||
      workshopEndWithQa?.endTime ||
      initialEnd.endTime,
  );
  const [durationMinutes, setDurationMinutes] = useState(String(initialDuration));
  const [qaDurationMinutes, setQaDurationMinutes] = useState(
    String(initialQaMinutes),
  );
  const [error, setError] = useState<string | null>(null);
  const [showConfirm, setShowConfirm] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const isLiveKitProvider =
    !createDraft?.sessionProvider || createDraft.sessionProvider === 'livekit';
  const [waitingRoomEnabled, setWaitingRoomEnabled] = useState(
    Boolean(createDraft?.waitingRoomEnabled),
  );
  const [recordingEnabled, setRecordingEnabled] = useState(
    createDraft?.recordingEnabled !== false,
  );
  const [trainerDefaultMic, setTrainerDefaultMic] = useState(
    Boolean(createDraft?.trainerDefaultMic),
  );
  const [trainerDefaultCamera, setTrainerDefaultCamera] = useState(
    Boolean(createDraft?.trainerDefaultCamera),
  );
  const [studentDefaultMic, setStudentDefaultMic] = useState(
    Boolean(createDraft?.studentDefaultMic),
  );
  const [studentDefaultCamera, setStudentDefaultCamera] = useState(
    Boolean(createDraft?.studentDefaultCamera),
  );
  const [practiceTestEnabled, setPracticeTestEnabled] = useState(
    Boolean(createDraft?.practiceTestEnabled),
  );

  // Persist Schedule fields so back/forward restores them until create succeeds.
  useEffect(() => {
    if (!adminCreateFlow) return;
    const draft = loadCreateSessionDraft();
    if (!draft) return;
    const contentMins = parseDurationInput(durationMinutes);
    const qaMins = Number.parseInt(qaDurationMinutes, 10);
    patchCreateSessionDraft({
      startDate,
      startTime,
      endDate,
      endTime,
      durationMinutes:
        contentMins != null && contentMins > 0
          ? contentMins
          : draft.durationMinutes,
      daySchedules: isWorkshop
        ? mergeDayTimings(workshopDayCount, startDate, startTime, dayTimings)
        : draft.daySchedules,
      waitingRoomEnabled,
      recordingEnabled,
      trainerDefaultMic,
      trainerDefaultCamera,
      studentDefaultMic,
      studentDefaultCamera,
      practiceTestEnabled,
      qaDurationMinutes:
        Number.isFinite(qaMins) && qaMins > 0
          ? qaMins
          : draft.qaDurationMinutes ?? defaultSessionForm.qaDurationMinutes,
    });
  }, [
    adminCreateFlow,
    startDate,
    startTime,
    endDate,
    endTime,
    durationMinutes,
    qaDurationMinutes,
    dayTimings,
    isWorkshop,
    workshopDayCount,
    waitingRoomEnabled,
    recordingEnabled,
    trainerDefaultMic,
    trainerDefaultCamera,
    studentDefaultMic,
    studentDefaultCamera,
    practiceTestEnabled,
  ]);

  const subjectIdForName =
    (createDraft?.subjectIds || []).map((id) => String(id || '').trim()).find(Boolean) ||
    'Session';
  const category: SessionCategory = createDraft?.sessionCategory ?? 'mandatory';
  const [autoSessionTitle, setAutoSessionTitle] = useState(
    createDraft?.autoSessionName || '',
  );

  useEffect(() => {
    if (!createDraft) {
      setAutoSessionTitle('');
      return;
    }
    let cancelled = false;
    (async () => {
      const name = await resolveLiveSessionName({
        category,
        subjectName: subjectIdForName,
        startDate,
        batchId: createDraft.batchId,
        courseId: createDraft.courseId,
        courseName: createDraft.courseName,
        sessionProvider: createDraft.sessionProvider,
      });
      if (!cancelled) setAutoSessionTitle(name);
    })();
    return () => {
      cancelled = true;
    };
  }, [createDraft, category, subjectIdForName, startDate]);

  useEffect(() => {
    if (!workshopSchedule) return;
    setDurationMinutes(String(workshopSchedule.totalDurationMinutes));
    const qa = Number.parseInt(qaDurationMinutes, 10);
    const qaMins =
      Number.isFinite(qa) && qa > 0
        ? clampQaDurationMinutes(qa)
        : defaultSessionForm.qaDurationMinutes;
    const withQa = addMinutesToStart(
      workshopSchedule.endDate,
      workshopSchedule.endTime,
      qaMins,
    );
    setEndDate(withQa.endDate);
    setEndTime(withQa.endTime);
  }, [workshopSchedule]);

  const parsedDurationMinutes = parseDurationInput(durationMinutes);
  const durationTooShort =
    parsedDurationMinutes !== null && parsedDurationMinutes < plannedMinutes;
  const durationInvalid =
    durationMinutes.trim() !== '' && parsedDurationMinutes === null;

  const parsedQaDurationMinutes = Number.parseInt(qaDurationMinutes, 10);
  const qaDurationInvalid =
    qaDurationMinutes.trim() === '' ||
    Number.isNaN(parsedQaDurationMinutes) ||
    parsedQaDurationMinutes < MIN_QA_DURATION_MINUTES ||
    parsedQaDurationMinutes > MAX_QA_DURATION_MINUTES;

  const resolvedQaMinutes = qaDurationInvalid
    ? 0
    : clampQaDurationMinutes(parsedQaDurationMinutes);

  const trainerId = createDraft?.trainerId || '';
  const availabilityEnd =
    endDate && endTime
      ? { endDate, endTime }
      : startDate && startTime && parsedDurationMinutes !== null && !qaDurationInvalid
        ? addMinutesToStart(
            startDate,
            startTime,
            totalSessionSpanMinutes(parsedDurationMinutes, resolvedQaMinutes),
          )
        : { endDate: '', endTime: '' };

  const [trainerAvailability, setTrainerAvailability] = useState<{
    status: 'idle' | 'checking' | 'available' | 'unavailable' | 'error';
    message: string;
  }>({ status: 'idle', message: '' });

  const createdSessionId =
    createdOptionalSession?.sessionId ||
    createdWorkshopSession?.sessionId ||
    manageStudentsSession?.id ||
    null;

  useEffect(() => {
    if (!shouldCheckTrainerAvailability(createdSessionId)) {
      setTrainerAvailability({
        status: 'available',
        message: 'Trainer is available for this time slot.',
      });
      return;
    }
    if (
      !trainerId ||
      !startDate ||
      !startTime ||
      !availabilityEnd.endDate ||
      !availabilityEnd.endTime
    ) {
      setTrainerAvailability({ status: 'idle', message: '' });
      return;
    }
    let cancelled = false;
    setTrainerAvailability({ status: 'checking', message: '' });
    const timer = setTimeout(async () => {
      try {
        // ---------------------------------------------------------------
        // step 1: Check trainer slot; ignore same-title self conflict
        // ---------------------------------------------------------------
        const response = await authService.post('/trainer/availability/', {
          trainer_id: trainerId,
          start_date: startDate,
          end_date: availabilityEnd.endDate,
          start_time: startTime.length === 5 ? `${startTime}:00` : startTime,
          end_time:
            availabilityEnd.endTime.length === 5
              ? `${availabilityEnd.endTime}:00`
              : availabilityEnd.endTime,
          session_id: '',
        });
        if (cancelled) return;
        const rawConflicts = response?.data?.list_of_sessions || [];
        const selfId = conflictSessionId(
          findSelfTrainerConflict(rawConflicts, autoSessionTitle),
        );
        let blocking = otherTrainerConflicts(rawConflicts, autoSessionTitle);

        if (selfId && blocking.length === 0 && response?.data?.success !== true) {
          const retry = await authService.post('/trainer/availability/', {
            trainer_id: trainerId,
            start_date: startDate,
            end_date: availabilityEnd.endDate,
            start_time: startTime.length === 5 ? `${startTime}:00` : startTime,
            end_time:
              availabilityEnd.endTime.length === 5
                ? `${availabilityEnd.endTime}:00`
                : availabilityEnd.endTime,
            session_id: selfId,
          });
          if (cancelled) return;
          if (retry?.data?.success === true) {
            setTrainerAvailability({
              status: 'available',
              message: 'Trainer is available for this time slot.',
            });
            return;
          }
          blocking = otherTrainerConflicts(
            retry?.data?.list_of_sessions || [],
            autoSessionTitle,
          );
        }

        if (response?.data?.success === true || blocking.length === 0) {
          setTrainerAvailability({
            status: 'available',
            message: 'Trainer is available for this time slot.',
          });
        } else {
          setTrainerAvailability({
            status: 'unavailable',
            message:
              response?.data?.message || 'Trainer is not available at this time.',
          });
        }
      } catch {
        if (!cancelled) {
          setTrainerAvailability({
            status: 'error',
            message: 'Could not verify trainer availability.',
          });
        }
      }
    }, 500);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [
    trainerId,
    startDate,
    startTime,
    availabilityEnd.endDate,
    availabilityEnd.endTime,
    autoSessionTitle,
    createdSessionId,
  ]);

  const canSave =
    createDraft !== null &&
    Boolean(startDate && startTime) &&
    parsedDurationMinutes !== null &&
    parsedDurationMinutes >= plannedMinutes &&
    !durationInvalid &&
    !qaDurationInvalid &&
    trainerAvailability.status !== 'unavailable' &&
    trainerAvailability.status !== 'checking';

  const durationFieldError = durationTooShort
    ? `Duration cannot be less than the planned session time (${formatDurationMinutes(plannedMinutes)}).`
    : durationInvalid
      ? 'Enter a valid duration (e.g. 55, 45m, or 1h 30m).'
      : null;

  const syncEndFromStartAndDuration = (
    nextStartDate: string,
    nextStartTime: string,
    durationValue: string,
    qaValue: string = qaDurationMinutes,
  ) => {
    const qaParsed = Number.parseInt(qaValue, 10);
    const qaMins =
      Number.isFinite(qaParsed) &&
      qaParsed >= MIN_QA_DURATION_MINUTES &&
      qaParsed <= MAX_QA_DURATION_MINUTES
        ? clampQaDurationMinutes(qaParsed)
        : 0;

    if (isWorkshop && createDraft) {
      const durationByTopic = Object.fromEntries(
        topics
          .filter((t) => t.durationMinutes > 0)
          .map((t) => [t.title, t.durationMinutes]),
      );
      const schedule = computeWorkshopSchedule(
        nextStartDate,
        nextStartTime,
        createDraft.dayTopics,
        {
          dayTimings: mergeDayTimings(
            workshopDayCount,
            nextStartDate,
            nextStartTime,
            dayTimings,
          ),
          durationByTopic,
        },
      );
      const parsed = parseDurationInput(durationValue);
      let baseEndDate = schedule.endDate;
      let baseEndTime = schedule.endTime;
      if (parsed !== null && parsed > schedule.totalDurationMinutes) {
        const lastDay = schedule.daySchedules[schedule.daySchedules.length - 1];
        if (lastDay) {
          const extraMinutes = parsed - schedule.totalDurationMinutes;
          const lastDayEnd = addMinutesToStart(
            lastDay.startDate,
            lastDay.startTime,
            Math.max(lastDay.durationMinutes + extraMinutes, lastDay.durationMinutes),
          );
          baseEndDate = lastDayEnd.endDate;
          baseEndTime = lastDayEnd.endTime;
        }
      }
      const withQa = addMinutesToStart(baseEndDate, baseEndTime, qaMins);
      setEndDate(withQa.endDate);
      setEndTime(withQa.endTime);
      return;
    }
    const parsed = parseDurationInput(durationValue);
    if (parsed === null) return;
    const next = addMinutesToStart(
      nextStartDate,
      nextStartTime,
      totalSessionSpanMinutes(parsed, qaMins),
    );
    setEndDate(next.endDate);
    setEndTime(next.endTime);
  };

  const handleStartDateChange = (value: string) => {
    setStartDate(value);
    if (isWorkshop) {
      setDayTimings((prev) =>
        mergeDayTimings(workshopDayCount, value, startTime, prev).map((day) =>
          day.dayIndex === 0 ? { ...day, startDate: value } : day,
        ),
      );
    }
    syncEndFromStartAndDuration(value, startTime, durationMinutes);
  };

  const handleStartTimeChange = (value: string) => {
    setStartTime(value);
    if (isWorkshop) {
      setDayTimings((prev) =>
        mergeDayTimings(workshopDayCount, startDate, value, prev).map((day) =>
          day.dayIndex === 0 ? { ...day, startTime: value } : day,
        ),
      );
    }
    syncEndFromStartAndDuration(startDate, value, durationMinutes);
  };

  const handleDayTimingChange = (
    dayIndex: number,
    patch: { startDate?: string; startTime?: string },
  ) => {
    const next = mergeDayTimings(
      workshopDayCount,
      startDate,
      startTime,
      dayTimings,
    ).map((day) =>
      day.dayIndex === dayIndex
        ? {
            ...day,
            startDate: patch.startDate ?? day.startDate,
            startTime: patch.startTime ?? day.startTime,
          }
        : day,
    );
    setDayTimings(next);

    const day0 = next[0];
    if (dayIndex === 0 && day0) {
      const nextDate = patch.startDate ?? day0.startDate;
      const nextTime = patch.startTime ?? day0.startTime;
      setStartDate(nextDate);
      setStartTime(nextTime);
      syncEndFromStartAndDuration(nextDate, nextTime, durationMinutes);
    }

    if (createDraft) {
      saveCreateSessionDraft({
        ...createDraft,
        daySchedules: next,
        startDate: next[0]?.startDate || createDraft.startDate,
        startTime: next[0]?.startTime || createDraft.startTime,
      });
    }
  };

  const handleDurationChange = (value: string) => {
    setDurationMinutes(value);
    const parsed = parseDurationInput(value);
    if (parsed === null) return;
    syncEndFromStartAndDuration(startDate, startTime, value);
  };

  const handleQaDurationChange = (value: string) => {
    setQaDurationMinutes(value);
    syncEndFromStartAndDuration(startDate, startTime, durationMinutes, value);
  };

  const handleEndDateChange = (value: string) => {
    setEndDate(value);
    if (isWorkshop) return;
    const span = minutesBetweenStartAndEnd(startDate, startTime, value, endTime);
    if (span === null) return;
    const contentMinutes = Math.max(0, span - resolvedQaMinutes);
    if (contentMinutes > 0) setDurationMinutes(String(contentMinutes));
  };

  const handleEndTimeChange = (value: string) => {
    setEndTime(value);
    if (isWorkshop) return;
    const span = minutesBetweenStartAndEnd(startDate, startTime, endDate, value);
    if (span === null) return;
    const contentMinutes = Math.max(0, span - resolvedQaMinutes);
    if (contentMinutes > 0) setDurationMinutes(String(contentMinutes));
  };

  const formatPreviewDateTime = (date: Date): string =>
    date.toLocaleString([], {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });

  const dateTimePreview =
    parsedDurationMinutes !== null
      ? (() => {
          const start = new Date(`${startDate}T${startTime}:00`);
          if (Number.isNaN(start.getTime())) return null;
          const end = isWorkshop
            ? new Date(`${endDate}T${endTime}:00`)
            : new Date(
                start.getTime() +
                  totalSessionSpanMinutes(parsedDurationMinutes, resolvedQaMinutes) *
                    60_000,
              );
          if (Number.isNaN(end.getTime())) return null;
          return { start, end };
        })()
      : null;

  const persistSession = async () => {
    if (!createDraft || !canSave || parsedDurationMinutes === null || qaDurationInvalid) return;

    const resolvedEnd =
      endDate && endTime
        ? { endDate, endTime }
        : addMinutesToStart(
            startDate,
            startTime,
            totalSessionSpanMinutes(parsedDurationMinutes, resolvedQaMinutes),
          );
    const qaMinutesForCreate = clampQaDurationMinutes(parsedQaDurationMinutes);

    setIsCreating(true);
    setError(null);
    const result = await createLiveKitSessionFromDraft({
      draft: {
        ...createDraft,
        daySchedules: isWorkshop ? dayTimings : createDraft.daySchedules,
        waitingRoomEnabled,
        recordingEnabled,
        trainerDefaultMic,
        trainerDefaultCamera,
        studentDefaultMic,
        studentDefaultCamera,
        practiceTestEnabled,
      },
      title: autoSessionTitle,
      startDate,
      startTime,
      endDate: resolvedEnd.endDate,
      endTime: resolvedEnd.endTime,
      qaDurationMinutes: qaMinutesForCreate,
    });
    setIsCreating(false);

    if (!result.ok) {
      setError(result.error);
      setShowConfirm(false);
      return;
    }

    if (result.sessionId > 0) {
      setQaDurationForSession(String(result.sessionId), qaMinutesForCreate);
    }

    const isOptional = createDraft.sessionCategory === 'optional';
    const createdMeta = {
      sessionId: result.sessionId,
      sessionName: autoSessionTitle,
      rolePrefix: result.rolePrefix,
      trackId: createDraft.trackId || '',
      courseId: createDraft.courseId || '',
      batchId: createDraft.batchId || '',
      batchIds:
        Array.isArray(createDraft.batchIds) && createDraft.batchIds.length > 0
          ? createDraft.batchIds
          : createDraft.batchId
            ? [createDraft.batchId]
            : [],
      subjects: createDraft.subjects || [],
    };

    setShowConfirm(false);

    // Optional sessions: keep draft until user picks Assign now / later,
    // otherwise clearing the flow remounts away and the popup never shows.
    if (isOptional && result.sessionId > 0) {
      setTrainerAvailability({
        status: 'available',
        message: 'Trainer is available for this time slot.',
      });
      setCreatedOptionalSession(createdMeta);
      setShowOptionalAssignPrompt(true);
      return;
    }

    // Online / onsite workshop: prompt to assign batches, then Assign Sessions flow.
    if (isWorkshop && result.sessionId > 0) {
      setTrainerAvailability({
        status: 'available',
        message: 'Trainer is available for this time slot.',
      });
      setCreatedWorkshopSession({
        sessionId: result.sessionId,
        sessionName: autoSessionTitle,
        rolePrefix: result.rolePrefix,
        trackId: createDraft.trackId || '',
        courseId: createDraft.courseId || '',
        sessionCategory: createDraft.sessionCategory || 'workshop_online',
        startDate,
        startTime,
      });
      setShowWorkshopBatchPrompt(true);
      return;
    }

    // Flag before clear so RequireCreateDraft / draft-guard don't bounce to Step 1.
    if (result.inviteType === 'personal' && result.sessionId > 0) {
      markCreateFlowExit('suppress');
      clearAdminCreateSessionFlow();
      navigate(`${result.rolePrefix}/add-students`, {
        replace: true,
        state: {
          sessionId: result.sessionId,
          sessionName: autoSessionTitle,
          fromCodeLive: true,
        },
      });
      return;
    }

    markCreateFlowExit('listing');
    clearAdminCreateSessionFlow();
    navigate(ROUTES.viewSessions, { replace: true });
  };

  const navigateToAssignStudents = () => {
    if (!createdOptionalSession) {
      markCreateFlowExit('listing');
      clearAdminCreateSessionFlow();
      navigate(ROUTES.viewSessions, { replace: true });
      return;
    }
    const {
      sessionId,
      sessionName,
      trackId,
      courseId,
      batchIds,
      batchId,
    } = createdOptionalSession;
    const batchesForAssign =
      batchIds.length > 0 ? batchIds : batchId ? [batchId] : [];

    // Open the same Manage students UI used on View Sessions (Add students tab).
    setManageStudentsSession({
      id: sessionId,
      Session_Topic: sessionName,
      Date: '',
      Start_Time: '',
      End_Time_date: '',
      End_Time_time: '',
      Expected_End_Time_date: '',
      Expected_End_Time_time: '',
      meetlink: '',
      conductedby: '',
      conductedby_email: '',
      co_trainers: [],
      attendances: {
        invited_students_count: 0,
        attended_students_count: 0,
      },
      session_status: 'scheduled',
      live_session_category: 'optional',
      track_id: trackId,
      course_id: courseId,
      batch_id: batchesForAssign.length === 1 ? batchesForAssign[0] : batchesForAssign,
    });
    setShowOptionalAssignPrompt(false);
  };

  const finishOptionalLater = () => {
    markCreateFlowExit('listing');
    clearAdminCreateSessionFlow();
    setShowOptionalAssignPrompt(false);
    setCreatedOptionalSession(null);
    setManageStudentsSession(null);
    navigate(ROUTES.viewSessions, { replace: true });
  };

  const finishWorkshopBatchLater = () => {
    markCreateFlowExit('listing');
    clearAdminCreateSessionFlow();
    setShowWorkshopBatchPrompt(false);
    setCreatedWorkshopSession(null);
    navigate(ROUTES.viewSessions, { replace: true });
  };

  const navigateToWorkshopBatchAssign = () => {
    if (!createdWorkshopSession) {
      finishWorkshopBatchLater();
      return;
    }
    const {
      sessionId,
      sessionName,
      trackId,
      courseId,
      sessionCategory,
      startDate: sessionStartDate,
      startTime: sessionStartTime,
      rolePrefix,
    } = createdWorkshopSession;

    markCreateFlowExit('suppress');
    clearAdminCreateSessionFlow();
    setShowWorkshopBatchPrompt(false);
    setCreatedWorkshopSession(null);
    navigate(`${rolePrefix}/assign-sessions`, {
      replace: true,
      state: {
        autoAssignSessionId: String(sessionId),
        sessionTitle: sessionName,
        trackId,
        courseId,
        liveSessionCategory: sessionCategory,
        startDate: sessionStartDate,
        startTime: sessionStartTime,
        fromWorkshopCreate: true,
      },
    });
  };

  const finishManageStudents = () => {
    markCreateFlowExit('listing');
    clearAdminCreateSessionFlow();
    setManageStudentsSession(null);
    setCreatedOptionalSession(null);
    setShowOptionalAssignPrompt(false);
    navigate(ROUTES.viewSessions, { replace: true });
  };

  const handleSubmit = () => {
    setError(null);
    if (trainerAvailability.status === 'unavailable') {
      setError(
        trainerAvailability.message || 'Trainer is not available at this time.',
      );
      return;
    }
    if (trainerAvailability.status === 'checking') {
      setError('Checking trainer availability — please wait a moment and try again.');
      return;
    }
    if (!canSave) {
      setError(
        durationFieldError ??
          (qaDurationInvalid
            ? `Enter a valid Q&A duration between ${MIN_QA_DURATION_MINUTES} and ${MAX_QA_DURATION_MINUTES} minutes.`
            : 'Enter a valid duration and end time.'),
      );
      return;
    }
    setShowConfirm(true);
  };

  if (!createDraft) {
    return null;
  }

  return (
    <AppLayout>
      <PageMain>
        <Link to={ROUTES.topicPlanner} className="codelive-wizard-back">
          <ArrowLeft className="h-4 w-4" />
          Back to Topic Planner
        </Link>

        <div className="mb-3">
          <label className="form-label">Session Name (auto-generated)</label>
          <div
            className="form-control font-monospace fw-semibold"
            style={{ backgroundColor: '#e8f5e9' }}
          >
            {autoSessionTitle}
          </div>
          <div className="form-text">{LIVE_SESSION_NAMING_CONVENTION}</div>
        </div>

        <div className="mb-3 rounded border bg-light p-3">
          <p className="mb-1 small">
            <span className="fw-semibold">Start:</span>{' '}
            {formatSessionDateTime(startDate, startTime)}
          </p>
          {isWorkshop && workshopSchedule ? (
            <div className="mt-2 pt-2 border-top">
              <p className="small fw-semibold text-warning-emphasis mb-2">
                Day-wise start date &amp; time
              </p>
              <p className="small text-muted mb-3">
                Set a specific date and start time for each workshop day.
              </p>
              {workshopSchedule.daySchedules.map((day) => (
                <div
                  key={day.dayIndex}
                  className="rounded border border-warning-subtle bg-white px-3 py-3 mb-2"
                >
                  <div className="d-flex flex-wrap justify-content-between gap-2 mb-2">
                    <p className="small fw-semibold mb-0">{day.dayLabel}</p>
                    <p className="small text-muted mb-0">
                      Ends {formatSessionDateTime(day.endDate, day.endTime)} ·{' '}
                      {formatDurationLabel(day.durationMinutes)}
                    </p>
                  </div>
                  <DateTimeField
                    label={`${day.dayLabel} start`}
                    date={day.startDate}
                    time={day.startTime}
                    onDateChange={(value) =>
                      handleDayTimingChange(day.dayIndex, { startDate: value })
                    }
                    onTimeChange={(value) =>
                      handleDayTimingChange(day.dayIndex, { startTime: value })
                    }
                  />
                  <p className="small text-muted mb-0 mt-2">
                    {day.topics.length > 0
                      ? day.topics.join(', ')
                      : 'No topics selected'}
                  </p>
                </div>
              ))}
              <p className="small text-muted mb-0">
                Total topic time: {formatDurationMinutes(plannedMinutes)} across{' '}
                {workshopSchedule.daySchedules.length} day
                {workshopSchedule.daySchedules.length === 1 ? '' : 's'}
              </p>
            </div>
          ) : (
            <p className="small text-muted mb-0">
              From planner: {formatDurationMinutes(plannedMinutes)} minimum
              {plannerDuration.quizMinutes > 0 &&
                ` (${formatDurationMinutes(plannerDuration.topicMinutes)} topics + ${formatDurationMinutes(plannerDuration.quizMinutes)} quizzes)`}
            </p>
          )}
        </div>

        <div className="row mb-3">
          <div className="col-md-6 mb-3 mb-md-0">
            <label className="form-label">Duration</label>
            <div className="d-flex gap-2">
              <input
                type="text"
                value={durationMinutes}
                onChange={(e) => handleDurationChange(e.target.value)}
                placeholder="e.g. 55, 45m, 1h 30m"
                className={`form-control ${durationFieldError ? 'is-invalid' : ''}`}
              />
              <select
                value={
                  durationOptions.some((option) => option.value === durationMinutes)
                    ? durationMinutes
                    : ''
                }
                onChange={(e) => {
                  if (e.target.value) handleDurationChange(e.target.value);
                }}
                className="form-select"
                style={{ maxWidth: '7rem' }}
                aria-label="Quick pick duration"
              >
                {!durationOptions.some((option) => option.value === durationMinutes) && (
                  <option value="" disabled hidden />
                )}
                {durationOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
            {parsedDurationMinutes !== null && (
              <div className="form-text">Selected: {formatDurationLabel(parsedDurationMinutes)}</div>
            )}
            {durationFieldError && (
              <div className="invalid-feedback d-block">{durationFieldError}</div>
            )}
          </div>

          <div className="col-md-6">
            <label className="form-label">Q&amp;A Duration</label>
            <div className="d-flex gap-2">
              <input
                type="number"
                min={MIN_QA_DURATION_MINUTES}
                max={MAX_QA_DURATION_MINUTES}
                step={5}
                value={qaDurationMinutes}
                onChange={(e) => handleQaDurationChange(e.target.value)}
                className={`form-control ${qaDurationInvalid ? 'is-invalid' : ''}`}
              />
              <select
                value={
                  qaDurationOptions.some((option) => option.value === qaDurationMinutes)
                    ? qaDurationMinutes
                    : ''
                }
                onChange={(e) => {
                  if (e.target.value) handleQaDurationChange(e.target.value);
                }}
                className="form-select"
                style={{ maxWidth: '7rem' }}
                aria-label="Quick pick Q&A duration"
              >
                {!qaDurationOptions.some((option) => option.value === qaDurationMinutes) && (
                  <option value="" disabled hidden />
                )}
                {qaDurationOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
            <div className={`form-text ${qaDurationInvalid ? 'text-danger' : ''}`}>
              {qaDurationInvalid
                ? `Enter ${MIN_QA_DURATION_MINUTES}–${MAX_QA_DURATION_MINUTES} minutes for the Q&A pause window.`
                : `Q&A window (${formatDurationLabel(
                    clampQaDurationMinutes(parsedQaDurationMinutes),
                  )}) is included in the session end time.`}
            </div>
          </div>
        </div>

        <div className="mb-3">
          <DateTimeField
            label={isWorkshop ? 'Workshop Day 1 start (overall session start)' : 'Start Date / Time'}
            date={startDate}
            time={startTime}
            onDateChange={handleStartDateChange}
            onTimeChange={handleStartTimeChange}
          />
        </div>

        <div className="mb-3">
          <DateTimeField
            label="End Date / Time"
            date={endDate}
            time={endTime}
            onDateChange={handleEndDateChange}
            onTimeChange={handleEndTimeChange}
          />
        </div>

        {isLiveKitProvider ? (
          <div className="mb-3 rounded border p-3">
            <p className="fw-semibold mb-1">LiveKit join defaults</p>
            <p className="small text-muted mb-3">
              Students and trainers see these mic/camera defaults on the pre-join
              device check. They can still change them before joining.
            </p>
            <div className="row g-3">
              <div className="col-md-6">
                <p className="small fw-semibold mb-2">Trainer defaults</p>
                <div className="form-check form-switch mb-2">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id="trainerDefaultMic"
                    checked={trainerDefaultMic}
                    onChange={(e) => setTrainerDefaultMic(e.target.checked)}
                  />
                  <label className="form-check-label" htmlFor="trainerDefaultMic">
                    Microphone on
                  </label>
                </div>
                <div className="form-check form-switch mb-2">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id="trainerDefaultCamera"
                    checked={trainerDefaultCamera}
                    onChange={(e) => setTrainerDefaultCamera(e.target.checked)}
                  />
                  <label className="form-check-label" htmlFor="trainerDefaultCamera">
                    Camera on
                  </label>
                </div>
              </div>
              <div className="col-md-6">
                <p className="small fw-semibold mb-2">Student defaults</p>
                <div className="form-check form-switch mb-2">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id="studentDefaultMic"
                    checked={studentDefaultMic}
                    onChange={(e) => setStudentDefaultMic(e.target.checked)}
                  />
                  <label className="form-check-label" htmlFor="studentDefaultMic">
                    Microphone on
                  </label>
                </div>
                <div className="form-check form-switch mb-2">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id="studentDefaultCamera"
                    checked={studentDefaultCamera}
                    onChange={(e) => setStudentDefaultCamera(e.target.checked)}
                  />
                  <label className="form-check-label" htmlFor="studentDefaultCamera">
                    Camera on
                  </label>
                </div>
              </div>
            </div>
            <hr className="my-3" />
            <div className="form-check form-switch">
              <input
                className="form-check-input"
                type="checkbox"
                id="waitingRoomEnabled"
                checked={waitingRoomEnabled}
                onChange={(e) => setWaitingRoomEnabled(e.target.checked)}
              />
              <label className="form-check-label" htmlFor="waitingRoomEnabled">
                Enable waiting room (students join muted until admitted)
              </label>
            </div>
            <div className="form-check form-switch mt-2">
              <input
                className="form-check-input"
                type="checkbox"
                id="recordingEnabled"
                checked={recordingEnabled}
                onChange={(e) => setRecordingEnabled(e.target.checked)}
              />
              <label className="form-check-label" htmlFor="recordingEnabled">
                Record this session
              </label>
            </div>
            <div className="form-check form-switch mt-2">
              <input
                className="form-check-input"
                type="checkbox"
                id="practiceTestEnabled"
                checked={practiceTestEnabled}
                onChange={(e) => setPracticeTestEnabled(e.target.checked)}
              />
              <label className="form-check-label" htmlFor="practiceTestEnabled">
                Assign a practice test
              </label>
            </div>
          </div>
        ) : null}

        {shouldCheckTrainerAvailability(createdSessionId) && trainerAvailability.status === 'checking' && (
          <div className="form-text mb-2">Checking trainer availability…</div>
        )}
        {shouldCheckTrainerAvailability(createdSessionId) && trainerAvailability.status === 'available' && (
          <div className="form-text text-success mb-2">{trainerAvailability.message}</div>
        )}
        {shouldCheckTrainerAvailability(createdSessionId) && trainerAvailability.status === 'unavailable' && (
          <div className="alert alert-danger py-2 mb-2" role="alert">
            {trainerAvailability.message}
          </div>
        )}
        {shouldCheckTrainerAvailability(createdSessionId) && trainerAvailability.status === 'error' && (
          <div className="form-text text-warning mb-2">{trainerAvailability.message}</div>
        )}

        {error && (
          <div className="alert alert-danger" role="alert">
            {error}
          </div>
        )}

        <div className="codelive-wizard-actions">
          <button
            type="button"
            onClick={handleSubmit}
            disabled={!canSave}
            className="btn btn-primary"
          >
            Create Session
          </button>
        </div>

        {showConfirm && (
          <div className="fixed inset-0 z-50 flex items-end justify-center bg-slate-900/40 p-3 sm:items-center sm:p-4">
            <div
              className={`flex w-full flex-col rounded-2xl bg-white shadow-xl ${
                isWorkshop ? 'max-w-3xl' : 'max-w-xl'
              }`}
              style={{ maxHeight: 'min(92dvh, 920px)' }}
              role="dialog"
              aria-modal="true"
              aria-labelledby="confirm-session-time-title"
            >
              <div className="shrink-0 border-b border-slate-100 px-4 pb-3 pt-4 sm:px-6 sm:pt-5">
                <h3
                  id="confirm-session-time-title"
                  className="text-lg font-bold text-slate-900"
                >
                  Confirm Session Time
                </h3>
                <p className="mt-1 text-sm text-slate-500">
                  Please confirm start, duration, Q&amp;A window, and end time
                  before creating the session.
                </p>
              </div>

              <div className="min-h-0 flex-1 overflow-y-auto px-4 py-4 sm:px-6">
                {dateTimePreview ? (
                  <div className="space-y-2 rounded-xl border border-slate-200 bg-slate-50 p-4 text-sm">
                    <p className="flex flex-wrap justify-between gap-2 sm:gap-3">
                      <span className="font-medium text-slate-600">Start</span>
                      <span className="text-right font-semibold text-slate-900">
                        {formatPreviewDateTime(dateTimePreview.start)}
                      </span>
                    </p>
                    <p className="flex flex-wrap justify-between gap-2 sm:gap-3">
                      <span className="font-medium text-slate-600">End</span>
                      <span className="text-right font-semibold text-slate-900">
                        {formatPreviewDateTime(dateTimePreview.end)}
                      </span>
                    </p>
                    <p className="flex flex-wrap justify-between gap-2 border-t border-slate-200 pt-2 sm:gap-3">
                      <span className="font-medium text-slate-600">Duration</span>
                      <span className="font-semibold text-slate-900">
                        {parsedDurationMinutes !== null
                          ? formatDurationLabel(parsedDurationMinutes)
                          : '—'}
                      </span>
                    </p>
                    <p className="flex flex-wrap justify-between gap-2 sm:gap-3">
                      <span className="font-medium text-slate-600">
                        Q&amp;A window
                      </span>
                      <span className="font-semibold text-slate-900">
                        {!qaDurationInvalid
                          ? formatDurationLabel(
                              clampQaDurationMinutes(parsedQaDurationMinutes),
                            )
                          : '—'}
                      </span>
                    </p>
                    {isWorkshop && workshopSchedule && (
                      <div className="space-y-2 border-t border-slate-200 pt-3">
                        <p className="text-xs font-bold uppercase tracking-wide text-amber-800">
                          Day-wise schedule
                        </p>
                        <div className="grid gap-2 sm:grid-cols-2">
                          {workshopSchedule.daySchedules.map((day) => (
                            <div
                              key={day.dayIndex}
                              className="rounded-lg border border-amber-100 bg-white px-3 py-2"
                            >
                              <p className="font-medium text-slate-800">
                                {day.dayLabel}:{' '}
                                {formatDurationLabel(day.durationMinutes)}
                              </p>
                              <p className="text-xs text-slate-500">
                                {formatSessionDateTime(
                                  day.startDate,
                                  day.startTime,
                                )}{' '}
                                →{' '}
                                {formatSessionDateTime(day.endDate, day.endTime)}
                              </p>
                              <p className="mt-1 text-xs text-slate-600">
                                {day.topics.join(', ')}
                              </p>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <p className="text-sm text-red-600">
                    Invalid duration or end time. Please update and try again.
                  </p>
                )}
              </div>

              <div className="shrink-0 border-t border-slate-100 bg-white px-4 py-3 sm:px-6">
                <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
                  <button
                    type="button"
                    onClick={() => setShowConfirm(false)}
                    disabled={isCreating}
                    className="btn btn-outline-secondary w-100 sm:w-auto"
                  >
                    Edit
                  </button>
                  <button
                    type="button"
                    onClick={async () => {
                      await persistSession();
                    }}
                    disabled={!dateTimePreview || isCreating}
                    className="btn btn-primary w-100 sm:w-auto"
                  >
                    {isCreating ? 'Creating…' : 'Confirm & Create'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {showOptionalAssignPrompt && createdOptionalSession && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4">
            <div className="w-full max-w-md rounded-2xl bg-white p-5 shadow-xl">
              <h3 className="text-lg font-bold text-slate-900">Add students?</h3>
              <p className="mt-2 text-sm text-slate-600">
                Optional session{' '}
                <span className="font-semibold text-slate-900">
                  {createdOptionalSession.sessionName}
                </span>{' '}
                was created successfully
                {createdOptionalSession.batchIds.length > 1
                  ? ` for ${createdOptionalSession.batchIds.length} batches`
                  : ''}
                . Add students to this session now, or do it later from View Sessions?
              </p>
              <div className="mt-5 flex flex-col gap-2 sm:flex-row sm:justify-end">
                <button
                  type="button"
                  onClick={finishOptionalLater}
                  className="btn btn-outline-secondary"
                >
                  Do it later
                </button>
                <button
                  type="button"
                  onClick={navigateToAssignStudents}
                  className="btn btn-primary"
                >
                  Add students now
                </button>
              </div>
            </div>
          </div>
        )}

        {showWorkshopBatchPrompt && createdWorkshopSession && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4">
            <div className="w-full max-w-md rounded-2xl bg-white p-5 shadow-xl">
              <h3 className="text-lg font-bold text-slate-900">Add batches?</h3>
              <p className="mt-2 text-sm text-slate-600">
                Workshop{' '}
                <span className="font-semibold text-slate-900">
                  {createdWorkshopSession.sessionName}
                </span>{' '}
                was created successfully. Assign batches to this session now, or
                do it later from Assign Sessions?
              </p>
              <div className="mt-5 flex flex-col gap-2 sm:flex-row sm:justify-end">
                <button
                  type="button"
                  onClick={finishWorkshopBatchLater}
                  className="btn btn-outline-secondary"
                >
                  Do it later
                </button>
                <button
                  type="button"
                  onClick={navigateToWorkshopBatchAssign}
                  className="btn btn-primary"
                >
                  Add batches now
                </button>
              </div>
            </div>
          </div>
        )}

        <ManageSessionStudentsModal
          open={Boolean(manageStudentsSession)}
          session={manageStudentsSession}
          initialTab={1}
          onClose={finishManageStudents}
          onChanged={() => {
            // Roster updated in-modal; stay open so user can keep adding/removing.
          }}
        />
      </PageMain>
    </AppLayout>
  );
}
